/**
 * SponsorsBlock — Tiered sponsor grid for hub pages.
 *
 * Renders sponsors grouped by tier (PRESENTING > SUPPORTING > COMMUNITY).
 * Each tier has distinct visual sizing:
 * - PRESENTING: Large logos, single row
 * - SUPPORTING: Medium logos, 2-column grid
 * - COMMUNITY: Small logos, 3-4 column grid
 *
 * Logos link to sponsor URLs when provided.
 */

import {ReactElement} from "react";
import {SurfaceSponsorItemResponse, SponsorTier} from "@/hooks/hub/useGetHub";
import "./SponsorsBlock.scss";

interface SponsorsBlockProps {
    sponsors?: SurfaceSponsorItemResponse[];
    sectionTitle?: string;
}

const TIER_ORDER: SponsorTier[] = ["PRESENTING", "SUPPORTING", "COMMUNITY"];

const TIER_LABELS: Record<SponsorTier, string> = {
    PRESENTING: "Presenting Sponsor",
    SUPPORTING: "Supporting Sponsors",
    COMMUNITY: "Community Partners",
};

export function SponsorsBlock({sponsors, sectionTitle}: SponsorsBlockProps): ReactElement {
    if (!sponsors || sponsors.length === 0) return <></>;

    // Group by tier, maintaining order within each tier
    const grouped = TIER_ORDER.reduce<Record<SponsorTier, SurfaceSponsorItemResponse[]>>(
        (acc, tier) => {
            acc[tier] = sponsors.filter(s => s.tier === tier);
            return acc;
        },
        {PRESENTING: [], SUPPORTING: [], COMMUNITY: []},
    );

    return (
        <div className="sponsors-block">
            {sectionTitle &&
                <h3 className="sponsors-block__section-title">{sectionTitle}</h3>
            }

            {TIER_ORDER.map(tier => {
                const items = grouped[tier];
                if (items.length === 0) return null;

                return (
                    <div key={tier} className={`sponsors-block__tier sponsors-block__tier--${tier.toLowerCase()}`}>
                        <p className="sponsors-block__tier-label">{TIER_LABELS[tier]}</p>
                        <div className="sponsors-block__grid">
                            {items.map(sponsor =>
                                <SponsorCard key={sponsor._id || sponsor.name} sponsor={sponsor} tier={tier} />)}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function SponsorCard({sponsor, tier}: { sponsor: SurfaceSponsorItemResponse; tier: SponsorTier }): ReactElement {
    const content =
        <div className={`sponsors-block__card sponsors-block__card--${tier.toLowerCase()}`}>
            {sponsor.logoImageUrl
                ? <img
                    className="sponsors-block__logo"
                    src={sponsor.logoImageUrl}
                    alt={sponsor.name}
                    loading="lazy"
                />
                : <span className="sponsors-block__name-fallback">{sponsor.name}</span>
            }
        </div>;
    if (sponsor.url) {
        // Validate URL safety — only allow http/https protocols (blocks javascript:, //evil.com, data:, etc.)
        const isSafe = (/^https?:\/\//i).test(sponsor.url);
        if (isSafe) {
            return (
                <a
                    href={sponsor.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="sponsors-block__link"
                >
                    {content}
                </a>
            );
        }
    }

    return content;
}
